import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Calculator, Zap, ArrowRight, AlertTriangle, Cable, Scale, ShieldCheck, ChevronDown } from "lucide-react";
import {
  ProjectMetaCard,
  useProjectMeta,
  escapeHtml,
  openPdfWindow,
  pdfHeader,
  pdfProjectCard,
  PDF_BASE_STYLES,
} from "@/lib/project-meta";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/vermogen-calculator")({
  head: () => {
    const url = `${SITE_URL}/vermogen-calculator`;
    const title =
      "EV Calculator — Bereken max kW en kabeldikte | Elvion Group Nederland";
    const description =
      "Bereken direct het maximale vermogen (kW) op basis van fasen en hoofdzekering en de juiste kabeldikte (mm²) volgens NEN 1010 met max 3% spanningsverlies.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: VermogenCalculatorPage,
});

// Standaard kabeldoorsneden (mm²) volgens NEN 1010 / IEC 60228
const STANDARD_CSA = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];

// Stroombelastbaarheid (A) bij installatiemethode B2 (kabel in buis, 30°C), koper
// Indicatieve waarden conform NEN 1010 — gebruik voor controle
const AMPACITY: Record<number, number> = {
  1.5: 17.5,
  2.5: 24,
  4: 32,
  6: 41,
  10: 57,
  16: 76,
  25: 101,
  35: 125,
  50: 151,
  70: 192,
  95: 232,
  120: 269,
  150: 309,
  185: 353,
  240: 415,
};

// Soortelijke weerstand koper (Ω·mm²/m) bij 20°C
const RHO_CU = 0.0175;

const FAQS = [
  {
    q: "Hoe bereken je het maximaal beschikbare vermogen?",
    a: "Bij 1-fase (230 V): P = U × I × cos φ. Bij 3-fase (400 V): P = √3 × U × I × cos φ. We rekenen standaard met cos φ = 0,95 (typisch voor moderne installaties met laadpalen, omvormers en LED).",
  },
  {
    q: "Wat is het verschil tussen 1-fase en 3-fase laden?",
    a: "1-fase laden gebruikt één fase + nul (230 V). Maximaal haalbaar: 5,7 kW bij 25 A of 8 kW bij 35 A. 3-fase laden gebruikt drie fasen (400 V) en kan tot 17,3 kW (3×25 A) of 24,2 kW (3×35 A) leveren. Voor sneller laden is 3-fase vrijwel altijd de voorkeur.",
  },
  {
    q: "Hoeveel kW heb ik minimaal nodig om thuis te laden?",
    a: "Vul laden (8–10 uur): 3,7 kW (1-fase 16 A) is voldoende. Normaal laden (4–6 uur): 7,4 kW (1-fase 32 A) of 11 kW (3-fase 16 A). Snelladen (2–3 uur): 22 kW (3-fase 32 A). Voor de meeste rijprofielen is 11 kW de sweet spot tussen snelheid en kosten.",
  },
  {
    q: "Moet ik mijn aansluiting verzwaren voor een laadpaal?",
    a: "Dat hangt af van je huidige zekering en het gewenste laadvermogen. Met 1×25 A kun je maximaal 5,7 kW gebruiken — genoeg voor een 3,7 kW of 7,4 kW laadpaal, maar niet voor 11 kW of 22 kW. Voor 11+ kW heb je minimaal 3×25 A nodig. Een verzwaring vraag je aan bij je netbeheerder en duurt doorgaans 2–6 weken.",
  },
  {
    q: "Wat is dynamische load balancing en waarom heb ik het nodig?",
    a: "Dynamische load balancing meet continu het totale verbruik van je huis en past het laadvermogen van de paal realtime aan. Zo voorkom je dat je hoofdzekering doorslaat als je oven, warmtepomp en wasmachine tegelijk draaien. Vooral bij 3×25 A of als je al zware verbruikers hebt, is dit sterk aan te raden.",
  },
  {
    q: "Kan ik een laadpaal op een normaal stopcontact aansluiten?",
    a: "Niet aanbevolen. Een schuko-stopcontact is bedoeld voor maximaal 10–16 A (2,3–3,7 kW) en biedt geen aardlekbeveiliging (type B) die voor EV-laden verplicht is. Bovendien raakt een stopcontact door continu zwaar beladen oververhit. Een eigen laadpaal is sneller, veiliger en goedkoper op de lange termijn.",
  },
  {
    q: "Kan ik zelf een laadpaal installeren?",
    a: "Nee — volgens de Wet op de Installaties in Woningen (WIW) en NEN 1010 mag alleen een erkend installateur vaste elektrische installaties aanleggen. Dit geldt zeker voor laadpalen, omdat ze een eigen groep, aardlekbeveiliging type B en vaak een verzwaarde aansluiting vereisen. Zelf doen is niet verzekerd en kan levensgevaarlijk zijn.",
  },
  {
    q: "Waarom is de kabeldikte afhankelijk van de lengte?",
    a: "Hoe langer de kabel, hoe groter de weerstand en dus het spanningsverlies. NEN 1010 staat maximaal 3% spanningsverlies toe op verlichtings- en krachtgroepen, en 5% op het complete netwerk. Bij grotere afstanden moet je dus een dikkere kabel kiezen om binnen de norm te blijven.",
  },
  {
    q: "Wat is een veilig spanningsverlies?",
    a: "NEN 1010 hanteert maximaal 3% spanningsverlies tussen meterkast en eindpunt voor lichtgroepen, en 5% voor krachtgroepen zoals laadpalen of motoren. Onze calculator rekent standaard met 3% — strenger dus veiliger.",
  },
  {
    q: "Welke kabeldikte heb ik nodig voor een laadpaal van 11 kW?",
    a: "Een 11 kW laadpaal trekt 16 A per fase (3-fase 400 V). Tot ±25 m volstaat 5×2,5 mm² koper. Bij 25–50 m kies je 5×4 mm², bij 50–80 m 5×6 mm². Onze calculator berekent dit exact voor jouw situatie.",
  },
  {
    q: "Welke kabeldikte voor een 22 kW laadpaal?",
    a: "Een 22 kW laadpaal trekt 32 A per fase. Tot ±20 m gebruik je 5×6 mm², tot 40 m kies je 5×10 mm² en bij grotere afstanden 5×16 mm². Houd ook rekening met de installatiemethode (in de grond, in buis, op kabelgoot) — die beïnvloedt de toegestane stroom.",
  },
  {
    q: "Wat betekent cos φ (arbeidsfactor)?",
    a: "Cos φ is de verhouding tussen werkelijk vermogen (kW) en schijnbaar vermogen (kVA). Bij puur ohmse belasting (verwarming, LED) is cos φ ≈ 1. Bij motoren en oudere installaties zakt dit naar 0,8–0,9. Onze calculator gebruikt 0,95 als praktische gemiddelde.",
  },
  {
    q: "Mag ik aluminium kabel gebruiken in plaats van koper?",
    a: "Aluminium heeft ongeveer 1,6× hogere weerstand dan koper. Voor dezelfde stroom heb je dus een dikkere doorsnede nodig (vuistregel: één maat groter). Aluminium wordt vooral gebruikt voor hoofdaansluitingen en grondkabels vanaf 16 mm². Deze calculator rekent met koper.",
  },
  {
    q: "Wat als mijn meterkast geen 3-fase aansluiting heeft?",
    a: "Veel oudere woningen hebben 1×25 A of 1×35 A (max 5,7–8 kW). Voor zware verbruikers zoals laadpalen, warmtepompen of inductie kun je laten verzwaren naar 3×25 A (17,3 kW) of 3×35 A (24,2 kW) via de netbeheerder.",
  },
];

const PHASE_PRESETS = [
  { phases: 1 as const, amps: 25, label: "1×25 A", kw: 5.7 },
  { phases: 1 as const, amps: 35, label: "1×35 A", kw: 8.0 },
  { phases: 3 as const, amps: 25, label: "3×25 A", kw: 17.3 },
  { phases: 3 as const, amps: 35, label: "3×35 A", kw: 24.2 },
  { phases: 3 as const, amps: 50, label: "3×50 A", kw: 34.6 },
  { phases: 3 as const, amps: 80, label: "3×80 A", kw: 55.4 },
];

type EvState = {
  phases: 1 | 3;
  amps: number;
  length: number;
  loadKw: number;
  cosPhi: number;
  maxDrop: number;
};

function buildEvPdfHtml(input: {
  projectNumber: string;
  customerName: string;
  projectDate: string;
  phases: 1 | 3;
  amps: number;
  length: number;
  loadKw: number;
  cosPhi: number;
  maxDrop: number;
  voltage: number;
  maxKw: number;
  currentForLoad: number;
  recommended: number;
  cores: number;
  actualDropPct: number;
  ampacity: number;
  overload: boolean;
}): string {
  return `<!doctype html><html lang="nl"><head><meta charset="utf-8" />
<title>Bestellijst laadpaal/vermogen ${escapeHtml(input.projectNumber || "")} — Elvion Group Nederland</title>
<style>${PDF_BASE_STYLES}</style></head><body>
${pdfHeader("Bestellijst vermogen & kabel")}
<h1>Bestellijst vermogen & kabeldikte</h1>
<div class="grid">
  ${pdfProjectCard({ projectNumber: input.projectNumber, customerName: input.customerName, projectDate: input.projectDate })}
  <div class="card">
    <h3>Installatie</h3>
    <div class="row"><span class="k">Aansluiting</span><span class="v">${input.phases}×${input.amps} A (${input.voltage} V)</span></div>
    <div class="row"><span class="k">Max beschikbaar</span><span class="v">${input.maxKw.toFixed(1)} kW</span></div>
    <div class="row"><span class="k">Gewenste belasting</span><span class="v">${input.loadKw.toFixed(1)} kW${input.overload ? " ⚠ overschrijdt aansluiting" : ""}</span></div>
    <div class="row"><span class="k">Kabellengte</span><span class="v">${input.length} m</span></div>
    <div class="row"><span class="k">cos φ / max drop</span><span class="v">${input.cosPhi} · ${input.maxDrop}%</span></div>
  </div>
</div>
<h2>Aanbevolen kabel (koper, NEN 1010)</h2>
<table>
  <thead><tr><th>Onderdeel</th><th class="num">Specificatie</th></tr></thead>
  <tbody>
    <tr><td><strong>Kabel</strong></td><td class="num">${input.cores}×${input.recommended} mm² Cu</td></tr>
    <tr><td>Stroom bij belasting</td><td class="num">${input.currentForLoad.toFixed(1)} A per fase</td></tr>
    <tr><td>Belastbaarheid kabel</td><td class="num">${input.ampacity} A</td></tr>
    <tr><td>Werkelijk spanningsverlies</td><td class="num">${input.actualDropPct.toFixed(2)} %</td></tr>
  </tbody>
</table>
<div class="total">
  <div class="lbl">Resultaat</div>
  <div class="val">${input.cores}×${input.recommended} mm² · ${input.maxKw.toFixed(1)} kW</div>
  <div style="font-size:10.5px; color:#5a6b7b; margin-top:4px;">Indicatieve berekening. Definitieve keuze (installatiemethode, bundeling, omgevingstemperatuur, aluminium vs koper) door erkend installateur conform NEN 1010.</div>
</div>
<p class="foot">Gegenereerd door de Elvion Group Nederland EV Vermogen Calculator.</p>
</body></html>`;
}

function VermogenCalculatorPage() {
  const [phases, setPhases] = useState<1 | 3>(3);
  const [amps, setAmps] = useState<number>(16);
  const [length, setLength] = useState<number>(20);
  const [loadKw, setLoadKw] = useState<number>(11);
  const [cosPhi, setCosPhi] = useState<number>(0.95);
  const [maxDrop, setMaxDrop] = useState<number>(3);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const meta = useProjectMeta<EvState>({
    storageKey: "vermogen-calculator:project:v1",
    autosaveTable: "vermogen_autosave",
    configurationsTable: "vermogen_configurations",
    getState: () => ({ phases, amps, length, loadKw, cosPhi, maxDrop }),
    applyState: (s) => {
      if (s.phases === 1 || s.phases === 3) setPhases(s.phases);
      if (typeof s.amps === "number") setAmps(s.amps);
      if (typeof s.length === "number") setLength(s.length);
      if (typeof s.loadKw === "number") setLoadKw(s.loadKw);
      if (typeof s.cosPhi === "number") setCosPhi(s.cosPhi);
      if (typeof s.maxDrop === "number") setMaxDrop(s.maxDrop);
    },
  });

  useEffect(() => {
    meta.pushAutosave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phases,
    amps,
    length,
    loadKw,
    cosPhi,
    maxDrop,
    meta.projectNumber,
    meta.customerName,
    meta.projectDate,
    meta.userId,
  ]);

  const result = useMemo(() => {
    const voltage = phases === 1 ? 230 : 400;
    // Max vermogen op basis van aansluiting (kW)
    const maxKw =
      phases === 1
        ? (voltage * amps * cosPhi) / 1000
        : (Math.sqrt(3) * voltage * amps * cosPhi) / 1000;

    // Stroom bij gewenste belasting (A per fase)
    const currentForLoad =
      phases === 1
        ? (loadKw * 1000) / (voltage * cosPhi)
        : (loadKw * 1000) / (Math.sqrt(3) * voltage * cosPhi);

    // Benodigde doorsnede op basis van spanningsverlies (mm²)
    // 1-fase: A = (2 × L × I × ρ × cos φ) / (U × ΔU%/100)
    // 3-fase: A = (√3 × L × I × ρ × cos φ) / (U × ΔU%/100)
    const allowedDropV = (voltage * maxDrop) / 100;
    const csaCalc =
      phases === 1
        ? (2 * length * currentForLoad * RHO_CU * cosPhi) / allowedDropV
        : (Math.sqrt(3) * length * currentForLoad * RHO_CU * cosPhi) /
          allowedDropV;

    // Kies eerstvolgende standaardmaat die zowel spanningsverlies als ampacity dekt
    const minByAmpacity = STANDARD_CSA.find(
      (c) => AMPACITY[c] >= currentForLoad,
    );
    const minByDrop = STANDARD_CSA.find((c) => c >= csaCalc);
    const recommendedIdx = Math.max(
      STANDARD_CSA.indexOf(minByAmpacity ?? STANDARD_CSA[0]),
      STANDARD_CSA.indexOf(minByDrop ?? STANDARD_CSA[0]),
    );
    const recommended = STANDARD_CSA[Math.max(0, recommendedIdx)];

    // Werkelijk spanningsverlies bij gekozen kabel
    const actualDropV =
      phases === 1
        ? (2 * length * currentForLoad * RHO_CU * cosPhi) / recommended
        : (Math.sqrt(3) * length * currentForLoad * RHO_CU * cosPhi) /
          recommended;
    const actualDropPct = (actualDropV / voltage) * 100;

    const overload = loadKw > maxKw;
    const cores = phases === 1 ? 3 : 5; // L+N+PE of 3L+N+PE

    return {
      voltage,
      maxKw,
      currentForLoad,
      csaCalc,
      recommended,
      actualDropPct,
      overload,
      cores,
      ampacity: AMPACITY[recommended],
    };
  }, [phases, amps, length, loadKw, cosPhi, maxDrop]);

  const printOrderPdf = () => {
    const html = buildEvPdfHtml({
      projectNumber: meta.projectNumber,
      customerName: meta.customerName,
      projectDate: meta.projectDate,
      phases,
      amps,
      length,
      loadKw,
      cosPhi,
      maxDrop,
      voltage: result.voltage,
      maxKw: result.maxKw,
      currentForLoad: result.currentForLoad,
      recommended: result.recommended,
      cores: result.cores,
      actualDropPct: result.actualDropPct,
      ampacity: result.ampacity,
      overload: result.overload,
    });
    openPdfWindow(html);
  };

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          EV Calculator: bereken max kW en kabeldikte
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Bereken in één oogopslag het <strong>maximaal beschikbare vermogen</strong>{" "}
          (kW) op basis van het aantal fasen en de hoofdzekering, en bepaal de{" "}
          <strong>juiste kabeldikte</strong> (mm²) op basis van de kabellengte en
          gewenste belasting conform NEN 1010 met maximaal 3% spanningsverlies.
        </p>

        {/* Intro quicknav */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Zap,
              title: "Max vermogen",
              text: "Bereken hoeveel kW je aansluiting aankan op 1- of 3-fase.",
            },
            {
              icon: Cable,
              title: "Kabeldikte",
              text: "Bepaal de juiste mm² op basis van lengte en belasting.",
            },
            {
              icon: Scale,
              title: "Spanningsverlies",
              text: "Controleer of je binnen de 3% NEN 1010 norm blijft.",
            },
            {
              icon: ShieldCheck,
              title: "Veilig",
              text: "Rekenmodel gebaseerd op erkende elektrische normen.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-orange/40"
            >
              <Icon className="size-6 text-orange" strokeWidth={2} />
              <h3 className="mt-3 text-sm font-bold text-foreground">{title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-text-secondary">{text}</p>
            </div>
          ))}
        </section>

        <div className="mt-6 flex flex-wrap gap-2">
          {[
            { label: "Calculator", href: "#calculator" },
            { label: "Referentietabel", href: "#referentie" },
            { label: "Formules & regels", href: "#formules" },
            { label: "Veelgestelde vragen", href: "#faq" },
          ].map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
            >
              {item.label}
              <ChevronDown className="size-3.5" />
            </a>
          ))}
        </div>

        <ProjectMetaCard {...meta} onPrintPdf={printOrderPdf} />

        <section id="calculator" className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          {/* Inputs */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Calculator
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-foreground">
                  Aantal fasen
                </label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[1, 3].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPhases(p as 1 | 3)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                        phases === p
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      {p === 1 ? "1-fase (230 V)" : "3-fase (400 V)"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">
                  Snelkeuze aansluiting
                </label>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {PHASE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        setPhases(p.phases);
                        setAmps(p.amps);
                      }}
                      className={`rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${
                        phases === p.phases && amps === p.amps
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      <span className="block">{p.label}</span>
                      <span className="block text-[10px] opacity-80">
                        ~{p.kw} kW
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Ampère per fase (hoofdzekering)
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {amps} A
                  </span>
                </label>
                <input
                  type="range"
                  min={6}
                  max={125}
                  step={1}
                  value={amps}
                  onChange={(e) => setAmps(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>6 A</span>
                  <span>16 / 25 / 35 / 50 A</span>
                  <span>125 A</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Kabellengte
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {length} m
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={250}
                  step={1}
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>1 m</span>
                  <span>50 m</span>
                  <span>250 m</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Gewenst vermogen (belasting)
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {loadKw.toFixed(1)} kW
                  </span>
                </label>
                <input
                  type="range"
                  min={0.5}
                  max={80}
                  step={0.5}
                  value={loadKw}
                  onChange={(e) => setLoadKw(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>0,5 kW</span>
                  <span>11 / 22 kW (laadpaal)</span>
                  <span>80 kW</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground">
                    cos φ (arbeidsfactor)
                  </label>
                  <select
                    value={cosPhi}
                    onChange={(e) => setCosPhi(Number(e.target.value))}
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  >
                    <option value={1}>1,00 — ohms (verwarming/LED)</option>
                    <option value={0.95}>0,95 — modern gemengd</option>
                    <option value={0.9}>0,90 — motoren licht</option>
                    <option value={0.8}>0,80 — motoren zwaar</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground">
                    Max. spanningsverlies
                  </label>
                  <select
                    value={maxDrop}
                    onChange={(e) => setMaxDrop(Number(e.target.value))}
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  >
                    <option value={1}>1% — kritisch (meet/data)</option>
                    <option value={3}>3% — NEN 1010 licht</option>
                    <option value={5}>5% — NEN 1010 kracht</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <Zap className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Resultaat
              </span>
            </div>

            <p className="text-sm text-text-secondary">
              Maximaal beschikbaar vermogen
            </p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground">
              {result.maxKw.toFixed(1)} kW
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              {phases}×{amps} A · {result.voltage} V · cos φ {cosPhi}
            </p>

            {result.overload && (
              <div className="mt-4 flex gap-2 rounded-md border border-[#3a7bd5]/40 bg-[#3a7bd5]/5 p-3 text-xs text-[#3a7bd5] dark:text-[#3a7bd5]">
                <AlertTriangle className="size-4 shrink-0" />
                <span>
                  De gewenste belasting ({loadKw} kW) overschrijdt het maximaal
                  beschikbare vermogen. Verzwaar de aansluiting of verlaag de
                  belasting (bv. via dynamische load balancing bij laadpalen).
                </span>
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Stroom bij belasting
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.currentForLoad.toFixed(1)} A
                </p>
                <p className="text-[10px] text-text-secondary">per fase</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Berekende doorsnede
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.csaCalc.toFixed(2)} mm²
                </p>
                <p className="text-[10px] text-text-secondary">
                  bij {maxDrop}% drop
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-orange/40 bg-orange/5 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-orange">
                Aanbevolen kabel (koper)
              </p>
              <p className="mt-1 text-2xl font-bold text-foreground">
                {result.cores}×{result.recommended} mm²
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                Werkelijk spanningsverlies: {result.actualDropPct.toFixed(2)}% ·
                Belastbaarheid kabel: {result.ampacity} A
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/info/$slug"
                params={{ slug: "laadpalen" }}
                className="inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange/90"
              >
                Meer over laadpalen
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/"
                hash="contact"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                Installatie aanvragen
              </Link>
            </div>
          </div>
        </section>

        {/* Reference table */}
        <section id="referentie" className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            Standaard kabeldoorsneden & belastbaarheid (koper, NEN 1010 — B2)
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Indicatieve waarden voor kabel in buis bij 30°C omgevingstemperatuur.
            Bij installatie in de grond of bundeling gelden correctiefactoren.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-3">Doorsnede</th>
                  <th className="px-4 py-3">Max. stroom</th>
                  <th className="px-4 py-3">Typisch gebruik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {STANDARD_CSA.slice(0, 11).map((c) => (
                  <tr
                    key={c}
                    className={c === result.recommended ? "bg-orange/5" : ""}
                  >
                    <td className="px-4 py-3 font-semibold text-foreground">
                      {c} mm²
                    </td>
                    <td className="px-4 py-3">{AMPACITY[c]} A</td>
                    <td className="px-4 py-3 text-text-secondary">
                      {c <= 2.5
                        ? "Verlichting & wandcontactdozen"
                        : c <= 6
                          ? "Krachtgroepen, kookplaat, 11 kW laadpaal"
                          : c <= 16
                            ? "22 kW laadpaal, warmtepomp, sauna"
                            : "Hoofdtoevoer, onderverdelers, zware machines"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Formulas */}
        <section id="formules" className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Gebruikte formules
            </h2>
            <ul className="mt-3 space-y-3 text-sm text-text-secondary">
              <li>
                <strong className="text-foreground">Vermogen 1-fase:</strong>{" "}
                P = U × I × cos φ → 230 × I × 0,95
              </li>
              <li>
                <strong className="text-foreground">Vermogen 3-fase:</strong>{" "}
                P = √3 × U × I × cos φ → 1,732 × 400 × I × 0,95
              </li>
              <li>
                <strong className="text-foreground">Doorsnede 1-fase:</strong>{" "}
                A = (2 × L × I × ρ × cos φ) / ΔU
              </li>
              <li>
                <strong className="text-foreground">Doorsnede 3-fase:</strong>{" "}
                A = (√3 × L × I × ρ × cos φ) / ΔU
              </li>
              <li>
                <strong className="text-foreground">ρ koper:</strong> 0,0175
                Ω·mm²/m bij 20°C
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Belangrijk</h2>
            <ul className="mt-3 space-y-2">
              {[
                "Deze calculator is een indicatie — laat installatie altijd uitvoeren door een erkend installateur (NEN 1010).",
                "Houd rekening met installatiemethode: grond, buis, bundel of vrij in lucht beïnvloeden de toegestane stroom.",
                "Bij meerdere kabels in één buis geldt een reductiefactor van 0,7–0,8.",
                "Voor laadpalen >11 kW is dynamische load balancing aanbevolen om groepsoverbelasting te voorkomen.",
                "Aluminium kabel? Kies één maat groter dan deze koperberekening.",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen over vermogen & kabeldikte
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && <p className="mt-2 text-text-secondary">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Hulp nodig bij installatie of berekening?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze erkende installateurs adviseren over zekeringwaarde, kabeltype
            en plaatsing — inclusief load balancing voor laadpalen en
            verzwaringsaanvraag bij de netbeheerder.
          </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/info/$slug"
                params={{ slug: "laadpalen" }}
                className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
              >
                Meer over laadpalen
              </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
